import { useEffect } from 'react';
import {
  absoluteUrl,
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  OG_IMAGE_ALT,
  OG_IMAGE_PATH,
  publicPage,
} from '../config/seo';

export type JsonLd = Record<string, unknown>;

export interface SeoOptions {
  /** Full document title (use `pageTitle()` for the `— CMSH.io` suffix). */
  title?: string;
  description?: string;
  /** Route path for the canonical / og:url. Defaults to the current pathname. */
  path?: string;
  /** Keep the page out of search indexes (auth flows, the signed-in app, error pages). */
  noindex?: boolean;
  /** Schema.org objects rendered as one `application/ld+json` script (several become an `@graph`). */
  jsonLd?: JsonLd | JsonLd[];
  ogType?: 'website' | 'article';
}

const INDEX_ROBOTS = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
const NOINDEX_ROBOTS = 'noindex, nofollow';
const JSON_LD_ATTR = 'data-seo-jsonld';

const head = () => document.head;

function upsert<T extends HTMLElement>(selector: string, create: () => T): T {
  const existing = head().querySelector<T>(selector);
  if (existing) return existing;
  const el = create();
  head().appendChild(el);
  return el;
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  const el = upsert<HTMLMetaElement>(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  });
  el.setAttribute('content', content);
}

function setCanonical(url: string) {
  const el = upsert<HTMLLinkElement>('link[rel="canonical"]', () => {
    const l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    return l;
  });
  el.setAttribute('href', url);
}

function setJsonLd(data: JsonLd | JsonLd[] | undefined) {
  head()
    .querySelectorAll(`script[${JSON_LD_ATTR}]`)
    .forEach((s) => s.remove());
  if (!data) return;
  const payload = Array.isArray(data) ? { '@context': 'https://schema.org', '@graph': data } : data;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSON_LD_ATTR, '');
  // `<` would end the script element early if a value ever contained `</script>`.
  script.text = JSON.stringify(payload).replace(/</g, '\\u003c');
  head().appendChild(script);
}

/** Write the full head snapshot for a page. Exported for tests and the pre-render step. */
export function applySeo(options: SeoOptions) {
  const title = options.title ?? DEFAULT_TITLE;
  const description = options.description ?? DEFAULT_DESCRIPTION;
  const url = absoluteUrl(options.path ?? window.location.pathname);

  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', options.noindex ? NOINDEX_ROBOTS : INDEX_ROBOTS);
  setCanonical(url);

  setMeta('property', 'og:type', options.ogType ?? 'website');
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', absoluteUrl(OG_IMAGE_PATH));
  setMeta('property', 'og:image:alt', OG_IMAGE_ALT);
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', absoluteUrl(OG_IMAGE_PATH));

  setJsonLd(options.jsonLd);
}

/**
 * Per-route SEO: title, description, canonical, robots, Open Graph / Twitter
 * cards and optional JSON-LD. Call it once at the top of every routed page.
 * Fields you omit fall back to the site defaults, so a page that only needs
 * `noindex` can pass just that. Pre-rendering (scripts/prerender.mjs) captures
 * the resulting head, so everything set here is also visible to crawlers that
 * do not execute JavaScript.
 */
export function useSeo(options: SeoOptions = {}) {
  const { title, description, path, noindex, ogType } = options;
  // Serialise so a freshly-built object literal does not re-run the effect every render.
  const jsonLdKey = options.jsonLd ? JSON.stringify(options.jsonLd) : '';

  useEffect(() => {
    applySeo({
      title,
      description,
      path,
      noindex,
      ogType,
      jsonLd: jsonLdKey ? (JSON.parse(jsonLdKey) as JsonLd | JsonLd[]) : undefined,
    });
    // On unmount, drop page-specific structured data so the next route starts clean.
    return () => setJsonLd(undefined);
  }, [title, description, path, noindex, ogType, jsonLdKey]);
}

/** Options for a catalogued public page: title, description and canonical from `PUBLIC_PAGES`. */
export function publicPageSeo(path: string, extra: Omit<SeoOptions, 'title' | 'description' | 'path'> = {}): SeoOptions {
  const page = publicPage(path);
  if (!page) throw new Error(`No PUBLIC_PAGES entry for ${path} (src/config/seo.ts)`);
  return { title: page.title, description: page.description, path: page.path, ...extra };
}
